import loadable from '@loadable/component';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FlatList, TouchableOpacity, View} from 'react-native';
import {useMutation} from 'react-query';
import {useDispatch, useSelector} from 'react-redux';
import {showAlert} from '../../../../../actions/AlertActions';
import {showSheet} from '../../../../../actions/SheetActions';
import CustomBottomSheet from '../../../../../components/bottom-sheet';
import Spacer from '../../../../../components/spacer/Spacer.component';
import {CustomText} from '../../../../../components/text/Text';
import {isWeb} from '../../../../../constants';
import useCameraPermission from '../../../../../hooks/useCameraPermission';
import LaunchGallery from '../../../../../imports/launch-gallery';
import {useHistory} from '../../../../../imports/Router';
import {useApi} from '../../../../../providers/ApiProvider';
import {selectrootsheettype} from '../../../../../selectors/SheetSelector';
import {selecetusertype} from '../../../../../selectors/User';
import {colorPalette} from '../../../../../theme';
import Option from '../../upload/components/option/Option.component';
import styles from './NationalRejectedSheet.style';
const SVG = loadable(() => import('../../../../../imports/svg'));

export default function NationalRejectedSheet() {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const {api} = useApi();
  const bottomSheetRef = useRef(null);
  const {sheetId} = useSelector(state => selectrootsheettype(state));
  const {userStatus} = useSelector(state => selecetusertype(state));
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false);
  const cameraPermission = useCameraPermission();
  const [data, setData] = useState({
    Instruction: [''],
    RejectNationalCardCause: '',
    UserStatus: 'profile_rejected',
  });

  useEffect(() => {
    switch (sheetId) {
      case 'nationalCardRejected':
        openHandler();
        break;
      default:
        closeHandler();
        break;
    }
  }, [sheetId]);

  useEffect(() => {
    if ( userStatus !== 'profile_rejected') {
      closeHandler();
    }
  }, [userStatus]);

  function openHandler() {
    setIsBottomSheetVisible(true);
    if (isWeb) return;
    bottomSheetRef.current?.expand();
  }

  function closeHandler() {
    setTimeout(() => {
      setIsBottomSheetVisible(false);
      dispatch(showSheet({sheetId: ''}));
    }, 400);
    switch (isWeb) {
      case true:
        bottomSheetRef?.current?.snapTo(() => 0);
        break;
      case false:
        bottomSheetRef.current?.close();
        break;
      default:
        break;
    }
  }

  function onRequestClose() {
    dispatch(showSheet({sheetId: ''}));
  }

  useEffect(() => {
    profileGetNationalCardStatusMutation.mutate();
  }, []);

  const profileGetNationalCardStatusMutation = useMutation({
    mutationFn: async () => {
      return api.profileGetNationalCardStatus();
    },
    onSuccess: response => {
      setData(response?.Data);
      if (response?.Data !== 'profile_rejected') {
        history.replace('/main/map');
        return;
      }
    },
    onError: () => {
      dispatch(
        showAlert({
          id: 'AlertBox',
          modalProps: {
            icon: 'redExclamation',
            message: t('an_unknown_error_occurred'),
            title : t('connection_error')
          },
        }),
      );
    },
  });

  const renderItem = useCallback(({item}) => {
    return (
      <CustomText size={14} font={'400'} color={colorPalette.grey8_5} mb={12}>
        {data?.Instruction?.length > 1 ? '•' : ''} {item}
      </CustomText>
    );
  }, []);

  function cameraHandler() {
    if (isWeb) {
      closeHandler();
      history.push('/register/upload-camera');
    } else {
      cameraPermission().then(() => {
        closeHandler();
        history.push('/register/upload-camera');
      });
    }
  }

  function galleryHandler(image) {
    closeHandler();
    history.push({
      pathname: '/register/upload-gallery',
      state: {
        image: image,
      },
    });
  }

  if (isBottomSheetVisible) {
    return (
      <CustomBottomSheet
        onClose={closeHandler}
        ref={bottomSheetRef}
        enablePanDownToClose={isWeb ? undefined : true}
        snapPoints={isWeb ? ({minHeight}) => [1, minHeight] : [520]}
        defaultSnap={isWeb ? 520 : undefined}
        open={true}
        onSpringEnd={
          isWeb
            ? () => {
                if (bottomSheetRef?.current?.height < 2) {
                  dispatch(showSheet({sheetId: ''}))
                }
              }
            : undefined
        }
        scrollViewStyle={isWeb ? undefined :styles.scroll}>
        <View style={styles.icon}>
          <SVG name={'closeCircleRed'} width={24} height={24} />
          <CustomText
            size={16}
            font={'700'}
            txtAlign="center"
            mt={8}
            color={colorPalette.grey9_5}>
            {t('national_card_photo_rejected')}
          </CustomText>
        </View>
        <View style={styles.reason}>
          <CustomText size={14} font={'400'} mb={8} color={colorPalette.grey6}>
            {t('reason_for_rejecting_photo')}
          </CustomText>
          <CustomText size={16} font={'700'} color={colorPalette.grey9}>
            {data?.RejectNationalCardCause}
          </CustomText>
        </View>
        {!profileGetNationalCardStatusMutation.isLoading &&
          data?.Instruction?.length > 0 && (
            <View style={styles.instruction}>
              <FlatList
                data={data?.Instruction}
                renderItem={renderItem}
                keyExtractor={item => item}
              />
            </View>
          )}
        <Spacer />
        <TouchableOpacity style={styles.stretch} onPress={cameraHandler}>
          <Option
            context={'take_photo_of_national_card'}
            containerStyle={styles.option}
            icon={'roundAltArrowLeft'}
          />
        </TouchableOpacity>
        <Option context={'from_gallery'} icon={'roundAltArrowLeft'}>
          <LaunchGallery onSelectImage={galleryHandler} />
        </Option>
      </CustomBottomSheet>
    );
  } else {
    return null;
  }
}
